import { memo, useCallback, useEffect } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import PropTypes from 'prop-types';
import { useSearchParams, useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';

import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import EmptyLinkedBankAccount from '#client/components/LinkBankAccountContainer/components/EmptyLinkedBankAccount';
import ListAccountSkeleton from '#client/components/ListAccountSkeleton';
import Account from '#client/components/MoveMoney/components/Account';
import RelinkAccount from '#client/components/MoveMoney/components/RelinkAccount';
import CheckDepositBanner from '#client/components/MoveMoney/DepositMoneyModal/components/CheckDepositBanner';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { COUNTERPARTY_ID, MODAL_NAMES } from '#client/constants/modals';
import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import { HOW_MUCH_STEP, NUMBER_OF_ACCOUNTS_TO_SHOW } from '#client/constants/transfer';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCounterparty from '#client/hooks/useCounterparty';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useRedirectToLinkedAccounts, {
  LINK_INTENT
} from '#client/hooks/useRedirectToLinkedAccounts';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import { getLast4digits } from '#client/utils/text';

import { DialogContentStyle, WrapperStyle } from './Common.styles';

const DialogContentStyled = styled(DialogContent)(DialogContentStyle);
const Wrapper = styled(Box)(WrapperStyle);

const ListAccounts = ({
  accounts,
  resetPagination,
  setShow,
  setHide,
  handleSelect,
  handleLinkBankAccount
}) => {
  const [searchParams] = useSearchParams();

  const hasCounterpartyId = searchParams.get(COUNTERPARTY_ID);
  const { setData } = useFormStep();

  useEffect(() => {
    if (hasCounterpartyId) {
      setData((prevData) => ({
        ...prevData,
        selectedFromAccount: {}
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCounterpartyId]);

  const isEmpty = !!accounts && accounts.length === 0;
  if (isEmpty) {
    return (
      <EmptyLinkedBankAccount
        handleLinkBankAccount={handleLinkBankAccount}
        hasLink={false}
      />
    );
  }

  return (
    <Stack gap={{ xs: '16px', md: '24px' }}>
      {accounts.map((account) => {
        const { id, accountNumber, connectedType, bank, accountType, status } = account;
        const last4Digits = getLast4digits(accountNumber) ?? '';

        if (status === COUNTER_PARTY_STATUS.ACTIVE) {
          return (
            <Account
              accountType={accountType}
              bankName={bank}
              connectedType={connectedType}
              handleSelect={() => handleSelect(account)}
              key={id}
              last4Digits={last4Digits}
              name={bank}
              numberOfCharacters={2}
            />
          );
        }

        return (
          <RelinkAccount
            accountType={accountType}
            bankName={bank}
            connectedType={connectedType}
            id={id}
            key={id}
            last4Digits={last4Digits}
            name={bank}
            numberOfCharacters={2}
            openingAt={MODAL_NAMES.DEPOSIT_MONEY}
            resetPagination={resetPagination}
            setHideSkeleton={setHide}
            setShowSkeleton={setShow}
            status={status}
          />
        );
      })}
    </Stack>
  );
};

ListAccounts.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      accountNumber: PropTypes.string.isRequired,
      connectedType: PropTypes.string.isRequired,
      bank: PropTypes.string.isRequired,
      accountType: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired
    })
  ).isRequired,
  resetPagination: PropTypes.func.isRequired,
  setShow: PropTypes.func.isRequired,
  setHide: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  handleLinkBankAccount: PropTypes.func.isRequired
};

function WhichAccountForm({ handleClose, isHandling = false }) {
  const navigate = useNavigate();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { redirectToLinkedAccounts } = useRedirectToLinkedAccounts(businessEntityUuid);
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const { clearAllToast } = useToastContext();
  const { setCurrentFormStep, setData, data: formData } = useFormStep();
  const { clearAllModal } = useModalContext();

  const {
    data,
    accounts,
    hasMore,
    loadingAccounts,
    isLoadingMore,
    loadMore,
    resetPagination,
    error: counterpartyError
  } = useCounterparty(COUNTERPARTY_CONNECTED_TYPE.PLAID, COUNTER_PARTY_STATUS.ALL, isHandling);

  const [showSkeleton, setShow, setHide] = useSkeleton(!loadingAccounts && !!data);
  const showLoadMoreButton = !showSkeleton && hasMore;

  useModalDisplayTracker({
    elementName: 'deposit_money_modal',
    appPageName: 'active_banking_dashboard',
    elementText: 'deposit_money_modal'
  });

  const trackClickCommon = {
    elementName: 'deposit_via_lnkacc',
    appPageName: 'deposit_money_modal'
  };

  const trackSelectClick = useButtonPressedCallback({
    ...trackClickCommon,
    elementText: 'send'
  });

  const trackAddNewClick = useButtonPressedCallback({
    ...trackClickCommon,
    elementName: 'add_new_lnkacc',
    elementText: 'Add new'
  });

  const handleSelect = useCallback(
    (selectedFromAccount) => {
      // close toast message linked bank account success
      clearAllToast();

      trackSelectClick();
      const othersAccounts = accounts
        .filter((account) => account.id !== selectedFromAccount.id)
        .slice(0, NUMBER_OF_ACCOUNTS_TO_SHOW - 1);
      setData({
        ...formData,
        selectedFromAccount,
        fromAccounts: [selectedFromAccount, ...othersAccounts],
        // Reset amount and description when selected another account
        amount: 0,
        description: null
      });
      setCurrentFormStep(HOW_MUCH_STEP);
    },
    [clearAllToast, trackSelectClick, accounts, setData, formData, setCurrentFormStep]
  );

  const handleLinkBankAccount = useCallback(() => {
    trackAddNewClick();
    clearAllModal();
    redirectToLinkedAccounts({ navigate, intent: LINK_INTENT });
  }, [clearAllModal, navigate, redirectToLinkedAccounts, trackAddNewClick]);

  // show skeleton when creating counterparty
  useEffect(() => {
    if (formData.isResetCounterparties) {
      setShow();
      resetPagination();
      setData({
        ...formData,
        isResetCounterparties: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    const { hasError } = formData || {};
    // close skeleton when has any error
    if (hasError) {
      setHide();
      setData({
        ...formData,
        hasError: false
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (counterpartyError) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartyError]);

  return (
    <DialogContentStyled>
      <ContainerGrid>
        <FocusGrid>
          <Wrapper>
            <Stack role="form">
              <Heading
                align="center"
                family="secondary"
                size="3xl"
                weight="bold"
              >
                Which account do you want to deposit from?
              </Heading>
              <Stack
                direction="row"
                justifyContent="right"
                margin={isDesktop ? '40px 0 24px 0' : '32px 0 16px 0'}
              >
                <Button
                  icon={<AddIcon />}
                  onClick={handleLinkBankAccount}
                  size="md"
                  variant="text"
                >
                  Add new
                </Button>
              </Stack>
              <Stack spacing="8px">
                {showSkeleton ? (
                  <ListAccountSkeleton />
                ) : (
                  <ListAccounts
                    accounts={accounts}
                    handleLinkBankAccount={handleLinkBankAccount}
                    handleSelect={handleSelect}
                    resetPagination={resetPagination}
                    setHide={setHide}
                    setShow={setShow}
                  />
                )}
              </Stack>
              <Box
                marginTop="40px"
                textAlign="center"
              >
                {showLoadMoreButton && (
                  <LoadingButton
                    loading={isLoadingMore}
                    onClick={loadMore}
                    variant="outlined"
                  >
                    Load more
                  </LoadingButton>
                )}
              </Box>
            </Stack>

            <CheckDepositBanner />
          </Wrapper>
        </FocusGrid>
      </ContainerGrid>
    </DialogContentStyled>
  );
}

export default memo(WhichAccountForm);

WhichAccountForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  isHandling: PropTypes.bool
};
